import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { GraduationCap, MapPin, BadgeCheck, Briefcase } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { getFaculty } from "../lib/api";

export default function Faculty() {
  const [list, setList] = useState([]);

  useEffect(() => {
    getFaculty().then(setList).catch(() => {});
  }, []);

  const teaching = list.filter((f) => f.staff_type !== "non_teaching");
  const nonTeaching = list.filter((f) => f.staff_type === "non_teaching");

  const renderCard = (f, i, variant = "teaching") => {
    const isPermanent = (f.appointment_status || "").toLowerCase().includes("permanent");
    return (
      <motion.div
        key={f.id}
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.4, delay: i * 0.04 }}
        className="bg-white rounded-3xl overflow-hidden border border-brand-primary/10 lift-card flex flex-col"
        data-testid={`faculty-card-${variant}-${i}`}
      >
        <div className="relative aspect-[4/3] bg-brand-accent overflow-hidden">
          {f.image ? (
            <img
              src={f.image}
              alt={f.name}
              className="w-full h-full object-cover"
              onError={(e) => { e.target.style.display = "none"; }}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-brand-primary/30">
              {variant === "non_teaching" ? (
                <Briefcase className="w-16 h-16" />
              ) : (
                <GraduationCap className="w-16 h-16" />
              )}
            </div>
          )}
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-brand-primary/85 via-brand-primary/40 to-transparent p-4">
            <div className="inline-block px-2.5 py-1 bg-brand-secondary text-brand-primary text-[10px] font-semibold tracking-[0.2em] uppercase rounded-full">
              {f.department}
            </div>
          </div>
        </div>
        <div className="p-5 lg:p-6 flex-1 flex flex-col">
          <h3 className="font-serif text-xl text-brand-primary mb-1">
            {f.name}
          </h3>
          <div className="text-xs tracking-[0.2em] uppercase text-brand-muted mb-3">
            {f.designation}
          </div>
          {f.qualifications && (
            <p className="text-sm text-brand-ink/75 mb-3">
              <span className="font-semibold text-brand-primary/80">Qualification: </span>
              {f.qualifications}
            </p>
          )}
          {f.address && (
            <p className="text-xs text-brand-muted mb-3 flex items-start gap-1.5">
              <MapPin className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-brand-secondary" />
              <span>{f.address}</span>
            </p>
          )}
          {f.appointment_status && (
            <div className="mt-auto pt-3 border-t border-brand-primary/10 flex items-center gap-2">
              <BadgeCheck className={`w-4 h-4 ${isPermanent ? "text-brand-primary" : "text-brand-tertiary"}`} />
              <span className={`text-[11px] font-medium tracking-wide ${isPermanent ? "text-brand-primary" : "text-brand-tertiary"}`}>
                {f.appointment_status}
              </span>
            </div>
          )}
        </div>
      </motion.div>
    );
  };

  return (
    <div data-testid="faculty-page">
      <PageHeader
        eyebrow="Our Faculty & Staff"
        title="Meet the people who make our college thrive."
        subtitle="A dedicated team of scholars, mentors, and support staff committed to academic excellence and a nurturing student experience."
      />

      {/* Teaching */}
      <section className="py-16 lg:py-24 bg-brand-ivory">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-10 max-w-2xl">
            <div className="eyebrow mb-2">Teaching Faculty</div>
            <h2 className="font-serif text-3xl sm:text-4xl text-brand-primary leading-tight">
              Educators who shape every classroom.
            </h2>
            <p className="text-brand-ink/70 text-sm leading-relaxed mt-2">
              {teaching.length} teaching members across departments — from Education and Sociology to Urdu, History, English and Economics.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 lg:gap-6" data-testid="teaching-grid">
            {teaching.map((f, i) => renderCard(f, i, "teaching"))}
          </div>
        </div>
      </section>

      {/* Non-Teaching */}
      {nonTeaching.length > 0 && (
        <section className="py-16 lg:py-24 bg-brand-accent/40" data-testid="non-teaching-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-10 max-w-2xl">
              <div className="eyebrow mb-2">Non-Teaching Staff</div>
              <h2 className="font-serif text-3xl sm:text-4xl text-brand-primary leading-tight">
                The team that keeps everything running.
              </h2>
              <p className="text-brand-ink/70 text-sm leading-relaxed mt-2">
                Our administrative and library staff — quietly powering the day-to-day life of the college.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6" data-testid="non-teaching-grid">
              {nonTeaching.map((f, i) => renderCard(f, i, "non_teaching"))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
